/**
 * WizardContext — lightweight self-contained state for WizardSlidePanel.
 *
 * Tracks the active step index, per-step validation flags, and submit state.
 * Intentionally minimal; avoids coupling to the legacy FormContext pattern.
 */
import React, {
  createContext,
  useContext,
  useReducer,
  Dispatch,
  PropsWithChildren,
} from "react";

// ─── State ────────────────────────────────────────────────────────────────────

export interface WizardState {
  /** Zero-based index of the currently visible step */
  currentStep: number;
  /** Map from step index → whether the Kendo Form is currently valid for that step */
  stepValidity: Record<number, boolean>;
  /** True while the final submit is in-flight */
  isSubmitting: boolean;
}

const createInitialState = (initialStep: number = 0): WizardState => ({
  currentStep: initialStep,
  stepValidity: {},
  isSubmitting: false,
});

// ─── Actions ──────────────────────────────────────────────────────────────────

export type WizardAction =
  | { type: "SET_STEP"; step: number }
  | { type: "SET_STEP_VALID"; step: number; valid: boolean }
  | { type: "SET_SUBMITTING"; value: boolean }
  | { type: "RESET"; initialStep?: number };

function reducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, currentStep: action.step };
    case "SET_STEP_VALID":
      return {
        ...state,
        stepValidity: { ...state.stepValidity, [action.step]: action.valid },
      };
    case "SET_SUBMITTING":
      return { ...state, isSubmitting: action.value };
    case "RESET":
      return createInitialState(action.initialStep ?? 0);
    default:
      return state;
  }
}

// ─── Contexts ─────────────────────────────────────────────────────────────────

const WizardStateContext = createContext<WizardState | undefined>(undefined);
const WizardDispatchContext = createContext<Dispatch<WizardAction> | undefined>(
  undefined,
);

// ─── Provider ─────────────────────────────────────────────────────────────────

interface WizardProviderProps extends PropsWithChildren {
  initialStep?: number;
}

export const WizardProvider: React.FC<WizardProviderProps> = ({
  children,
  initialStep = 0,
}) => {
  const [state, dispatch] = useReducer(
    reducer,
    createInitialState(initialStep),
  );
  return (
    <WizardStateContext.Provider value={state}>
      <WizardDispatchContext.Provider value={dispatch}>
        {children}
      </WizardDispatchContext.Provider>
    </WizardStateContext.Provider>
  );
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** Returns the current wizard state */
export const useWizardState = (): WizardState => {
  const ctx = useContext(WizardStateContext);
  if (!ctx)
    throw new Error("useWizardState must be used within WizardProvider");
  return ctx;
};

/** Returns the dispatch function for updating wizard state */
export const useWizardDispatch = (): Dispatch<WizardAction> => {
  const ctx = useContext(WizardDispatchContext);
  if (!ctx)
    throw new Error("useWizardDispatch must be used within WizardProvider");
  return ctx;
};
